/*
 * @flow strict
 * Copyright (C) 2019 MetaBrainz Foundation
 *
 * This file is part of MusicBrainz, the open internet music database,
 * and is licensed under the GPL version 2, or (at your option) any
 * later version: http://www.gnu.org/licenses/gpl-2.0.txt
 */

import {useMergeRefs} from '@floating-ui/react';
import * as Sentry from '@sentry/browser';
import * as React from 'react';

import ENTITIES from '../../../../../entities.mjs';
import AddEntityDialog from '../../edit/components/AddEntityDialog.js';
import {MBID_REGEXP} from '../constants.js';
import useOutsideClickEffect from '../hooks/useOutsideClickEffect.js';
import {unwrapNl} from '../i18n.js';
import {arraysEqual} from '../utility/arrays.js';
import clean from '../utility/clean.js';
import getCookie from '../utility/getCookie.js';
import isBlank from '../utility/isBlank.js';

import {
  CLOSE_ADD_ENTITY_DIALOG,
  HIDE_MENU,
  HIGHLIGHT_NEXT_ITEM,
  HIGHLIGHT_PREVIOUS_ITEM,
  SHOW_LOOKUP_ERROR,
  SHOW_LOOKUP_TYPE_ERROR,
  SHOW_MENU,
  SHOW_SEARCH_ERROR,
  STOP_SEARCH,
} from './Autocomplete2/actions.js';
import {
  SEARCH_PLACEHOLDERS,
} from './Autocomplete2/constants.js';
import formatItem, {
  type FormatOptionsT,
} from './Autocomplete2/formatters.js';
import {getOrFetchRecentItems} from './Autocomplete2/recentItems.js';
import {
  generateItems,
  generateStatusMessage,
} from './Autocomplete2/reducer.js';
import searchItems, {
  getItemName,
  indexItems,
} from './Autocomplete2/searchItems.js';
import type {
  ActionT,
  EntityItemT,
  ItemT,
  OptionItemT,
  PropsT,
  StateT,
} from './Autocomplete2/types.js';

/*
 * `doSearch` performs a direct or indexed search (via /ws/js). This is the
 * default behavior if no `items` prop is given.
 */
function doSearch<T: EntityItemT>(
  dispatch: (ActionT<T>) => void,
  state: StateT<T>,
  xhr: {current: XMLHttpRequest | null},
): void {
  const searchXhr = new XMLHttpRequest();
  xhr.current = searchXhr;

  searchXhr.addEventListener('load', () => {
    xhr.current = null;

    if (searchXhr.status !== 200) {
      dispatch(SHOW_SEARCH_ERROR);
      return;
    }

    const entities = JSON.parse(searchXhr.responseText);
    const pager: {+current: StrOrNum, +pages: StrOrNum} = entities.pop();
    const newPage = parseInt(pager.current, 10);
    const totalPages = parseInt(pager.pages, 10);

    dispatch({
      entities,
      page: newPage,
      totalPages,
      type: 'show-ws-results',
    });
  });

  const entityWebServicePath = state.entityType === 'editor'
    ? 'editor'
    : ENTITIES[state.entityType].url;

  if (entityWebServicePath == null) {
    throw new Error(
      'Can\'t build a web service URL for ' +
      JSON.stringify(state.entityType) +
      ' entities.',
    );
  }

  const url = (
    '/ws/js/' + entityWebServicePath +
    '/?q=' + encodeURIComponent(state.inputValue || '') +
    '&page=' + String(state.page) +
    '&direct=' + (state.indexedSearch ? 'false' : 'true')
  );

  searchXhr.open('GET', url);
  searchXhr.send();
}

function handleItemMouseDown(event: SyntheticMouseEvent<HTMLLIElement>) {
  event.preventDefault();
}

function setScrollPosition(menuId: string) {
  const menu = document.getElementById(menuId);
  if (!menu) {
    return;
  }
  const highlightedItem: HTMLLIElement | null =
    // $FlowExpectedError[incompatible-type]
    menu.querySelector('li[aria-selected=true]') ??
    // If there's no highlighted item, scroll to the top of the list.
    menu.querySelector('li');
  if (!highlightedItem) {
    return;
  }
  const position =
    (highlightedItem.offsetTop + (highlightedItem.offsetHeight / 2)) -
    menu.scrollTop;
  const middle = menu.offsetHeight / 2;
  if (position < middle) {
    menu.scrollTop -= (middle - position);
  }
  if (position > middle) {
    menu.scrollTop += (position - middle);
  }
}

type InitialStateT<T: EntityItemT> = {
  +canChangeType?: (string) => boolean,
  +containerClass?: string,
  +disabled?: boolean,
  +entityType: T['entityType'],
  +extractSearchTerms?: (OptionItemT<T>) => Array<string>,
  +htmlName?: string,
  +id: string,
  +inputChangeHook?: (
    inputValue: string,
    state: StateT<T>,
    selectItem: (OptionItemT<T>) => boolean,
  ) => boolean,
  +inputClass?: string,
  +inputRef?: {-current: HTMLInputElement | null},
  +inputValue?: string,
  +isLookupPerformed?: boolean,
  +label?: string,
  +placeholder?: string,
  +recentItemsKey?: string,
  +required?: boolean,
  +selectedItem?: OptionItemT<T> | null,
  +showLabel?: boolean,
  +staticItems?: $ReadOnlyArray<OptionItemT<T>>,
  +width?: string,
};

const EMPTY_ITEMS: $ReadOnlyArray<ItemT<empty>> = Object.freeze([]);

export function createInitialState<T: EntityItemT>(
  initialState: InitialStateT<T>,
): {...StateT<T>} {
  const {
    disabled = false,
    entityType,
    extractSearchTerms = getItemName,
    inputValue: initialInputValue,
    recentItemsKey,
    required = false,
    selectedItem,
    staticItems,
    ...restProps
  } = initialState;

  const inputValue =
    initialInputValue ??
    (selectedItem == null ? null : unwrapNl<string>(selectedItem.name)) ??
    '';

  if (staticItems) {
    indexItems(staticItems, extractSearchTerms);
  }

  let staticResults = staticItems ?? null;
  if (staticResults && nonEmpty(inputValue)) {
    staticResults = searchItems(staticResults, inputValue);
  }

  const state: {...StateT<T>} = {
    disabled,
    entityType,
    error: 0,
    highlightedIndex: -1,
    indexedSearch: true,
    inputValue,
    isInputFocused: false,
    isOpen: false,
    items: EMPTY_ITEMS,
    page: 1,
    pendingSearch: null,
    recentItems: null,
    recentItemsKey: recentItemsKey ?? entityType,
    required,
    results: staticResults,
    selectedItem: selectedItem ?? null,
    showDescriptions:
      getCookie('show_autocomplete_descriptions') !== 'false',
    staticItems,
    statusMessage: '',
    totalPages: null,
    ...restProps,
  };

  state.items = generateItems(state);
  state.statusMessage = generateStatusMessage(state);

  return state;
}

component _AutocompleteItem<T: EntityItemT>(
  autocompleteId: string,
  dispatch: (ActionT<T>) => void,
  formatOptions?: ?FormatOptionsT,
  index: number,
  isHighlighted: boolean,
  isSelected: boolean,
  item: ItemT<T>,
  selectItem: (ItemT<T>) => boolean,
) {
  const itemId = `${autocompleteId}-item-${item.id}`;
  const isDisabled = item.disabled === true;
  const isSeparator = item.separator === true;

  /*
   * `item.level` allows showing a hierarchy by indenting each option.
   * The first level (with normal padding) is 0. Each level increment
   * adds 8px to the left padding.
   */
  if (__DEV__) {
    invariant(item.level == null || item.level >= 0);
  }

  function handleItemClick() {
    if (!isDisabled) {
      selectItem(item);
    }
  }

  function handleItemMouseOver() {
    if (item.disabled !== true) {
      dispatch({index, type: 'highlight-index'});
    }
  }

  return (
    <li
      aria-disabled={isDisabled ? 'true' : 'false'}
      aria-selected={isHighlighted ? 'true' : 'false'}
      className={
        (isDisabled ? 'disabled ' : '') +
        (isHighlighted ? 'highlighted ' : '') +
        (isSelected ? 'selected ' : '') +
        (isSeparator ? 'separator ' : '') +
        (item.level != null && item.level > 0 ? `level${item.level} ` : '') +
        `${item.type}-item `
      }
      id={itemId}
      key={item.id}
      onClick={handleItemClick}
      onMouseDown={handleItemMouseDown}
      onMouseOver={handleItemMouseOver}
      role="option"
    >
      {formatItem<T>(item, formatOptions)}
    </li>
  );
}

const AutocompleteItem = React.memo(_AutocompleteItem);

component _Autocomplete2<T: EntityItemT>(...props: PropsT<T>) {
  const {dispatch, state} = props;

  const {
    canChangeType,
    containerClass,
    disabled = false,
    entityType,
    highlightedIndex,
    htmlName,
    id: inputId,
    inputChangeHook,
    inputRef: externalInputRef,
    inputValue,
    isAddEntityDialogOpen = false,
    isOpen,
    items,
    pendingSearch,
    selectedItem,
    staticItems,
    statusMessage,
  } = state;

  const xhr = React.useRef<XMLHttpRequest | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const inputRefCallback = useMergeRefs([inputRef, externalInputRef]);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const inputTimeout = React.useRef<TimeoutID | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const prevIsOpen = React.useRef<boolean>(false);
  const shouldUpdateScrollPositionRef = React.useRef<boolean>(false);

  const highlightedItem = highlightedIndex >= 0
    ? (items[highlightedIndex] ?? null)
    : null;

  function clearInputTimeout() {
    if (inputTimeout.current) {
      clearTimeout(inputTimeout.current);
      inputTimeout.current = null;
    }
  }

  const stopRequests = React.useCallback(() => {
    if (xhr.current) {
      xhr.current.abort();
      xhr.current = null;
    }

    clearInputTimeout();

    if (nonEmpty(pendingSearch)) {
      dispatch(STOP_SEARCH);
    }
  }, [dispatch, pendingSearch]);

  const selectItem = React.useCallback((
    item: ItemT<T>,
  ) => {
    const isDisabled = item.disabled === true;

    if (!isDisabled) {
      stopRequests();
      if (item.type === 'option') {
        const newEntityType = item.entity.entityType;
        if (newEntityType !== entityType) {
          if (canChangeType?.(newEntityType)) {
            dispatch({
              entityType: newEntityType,
              type: 'change-entity-type',
            });
          } else {
            return false;
          }
        }
      }
      dispatch({item, type: 'select-item'});
      return true;
    }
    return false;
  }, [
    stopRequests,
    entityType,
    canChangeType,
    dispatch,
  ]);

  function handleButtonClick() {
    inputRef.current?.focus();

    stopRequests();

    if (isOpen) {
      dispatch(HIDE_MENU);
    } else {
      showAvailableItemsOrBeginLookupOrSearch();
    }
  }

  function handleBlur() {
    if (isOpen) {
      setTimeout(() => {
        const container = containerRef.current;
        if (container && !container.contains(document.activeElement)) {
          stopRequests();
          dispatch(HIDE_MENU);
        }
      }, 1);
    }
  }

  function handleInputChange(
    event: SyntheticInputEvent<HTMLInputElement>,
  ) {
    const newInputValue = event.currentTarget.value;

    dispatch({type: 'type-value', value: newInputValue});

    if (isBlank(newInputValue)) {
      stopRequests();
      return;
    }

    if (
      inputChangeHook != null &&
      inputChangeHook(
        newInputValue,
        state,
        selectItem,
      )
    ) {
      return;
    }

    beginLookupOrSearch(inputValue, newInputValue);
  }

  function beginLookupOrSearch(
    oldInputValue: string,
    newInputValue: string,
  ) {
    const mbidMatch = newInputValue.match(MBID_REGEXP);
    if (mbidMatch) {
      /*
       * The user pasted an MBID (or a URL containing one). Perform a
       * direct lookup.
       */
      stopRequests();

      if (staticItems) {
        const option = staticItems.find((item) => (
          item.type === 'option' &&
          Object.hasOwn(item.entity, 'gid') &&
          item.entity.gid === mbidMatch[0]
        ));
        if (option) {
          selectItem(option);
        }
        return;
      }

      const lookupXhr = new XMLHttpRequest();
      xhr.current = lookupXhr;

      lookupXhr.addEventListener('load', () => {
        xhr.current = null;

        if (lookupXhr.status !== 200) {
          dispatch(SHOW_LOOKUP_ERROR);
          return;
        }

        const entity = JSON.parse(lookupXhr.responseText);
        const option: OptionItemT<T> = {
          entity,
          id: entity.id,
          name: entity.name,
          type: 'option',
        };

        if (!selectItem(option)) {
          dispatch(SHOW_LOOKUP_TYPE_ERROR);
        }
      });

      lookupXhr.open('GET', '/ws/js/entity/' + mbidMatch[0].toLowerCase());
      lookupXhr.send();
    } else if (oldInputValue !== newInputValue) {
      stopRequests();
      dispatch({
        searchTerm: clean(newInputValue),
        type: 'search-after-timeout',
      });
    }
  }

  function handleInputFocus() {
    dispatch({isFocused: true, type: 'set-input-focus'});
  }

  function handleInputBlur() {
    dispatch({isFocused: false, type: 'set-input-focus'});
  }

  function showAvailableItemsOrBeginLookupOrSearch() {
    /*
     * If there's an existing search term, there should be at least one
     * item even if there are no results (saying so). If there isn't,
     * the entity type probably changed; re-initiate the search with
     * the existing input value.
     */
    if (items.length) {
      dispatch(SHOW_MENU);
    } else if (!isBlank(inputValue)) {
      beginLookupOrSearch('', inputValue);
    }
  }

  function handleInputKeyDown(
    event: SyntheticKeyboardEvent<HTMLInputElement | HTMLButtonElement>,
  ) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();

        if (isOpen) {
          shouldUpdateScrollPositionRef.current = true;
          dispatch(HIGHLIGHT_NEXT_ITEM);
        } else {
          showAvailableItemsOrBeginLookupOrSearch();
        }
        break;

      case 'ArrowUp':
        if (isOpen) {
          event.preventDefault();
          shouldUpdateScrollPositionRef.current = true;
          dispatch(HIGHLIGHT_PREVIOUS_ITEM);
        }
        break;

      case 'Enter':
      case 'Tab': {
        if (isOpen && highlightedItem) {
          event.preventDefault();
          event.stopPropagation();
          selectItem(highlightedItem);
        }
        break;
      }

      case 'Escape':
        stopRequests();
        if (isOpen) {
          event.preventDefault();
          event.stopPropagation();
          dispatch(HIDE_MENU);
        }
        break;
    }
  }

  function handleMenuMouseDown(
    event: SyntheticMouseEvent<HTMLUListElement>,
  ) {
    /*
     * Clicking on the menu itself (including any scroll bar) should not
     * close the menu.  `preventDefault` here prevents a blur event on
     * the input.
     */
    event.preventDefault();
  }

  const handleOuterClick = React.useCallback(() => {
    stopRequests();
    if (isOpen) {
      dispatch(HIDE_MENU);
    }
  }, [stopRequests, isOpen, dispatch]);

  const closeAddEntityDialog = React.useCallback(() => {
    dispatch(CLOSE_ADD_ENTITY_DIALOG);
    setTimeout(function () {
      const input = inputRef.current;
      if (input) {
        input.focus();
      }
    }, 1);
  }, [dispatch, inputRef]);

  const addEntityDialogCallback = React.useCallback(
    (entity: NonUrlRelatableEntityT) => {
      invariant(
        entity?.entityType === entityType,
        'Wrong type of entity received',
      );
      const item = {
        // $FlowFixMe[incompatible-type]
        entity: (entity: T),
        id: entity.id,
        name: entity.name,
        type: 'option' as const,
      };
      dispatch({item, type: 'select-item'});
      closeAddEntityDialog();
    }, [closeAddEntityDialog, dispatch, entityType],
  );

  const activeDescendant = highlightedItem
    ? `${inputId}-item-${highlightedItem.id}`
    : null;
  const labelId = `${inputId}-label`;
  const menuId = `${inputId}-menu`;
  const statusId = `${inputId}-status`;

  useOutsideClickEffect(
    containerRef,
    handleOuterClick,
  );

  React.useEffect(() => {
    let cancelled = false;
    getOrFetchRecentItems<T>(
      entityType,
      state.recentItemsKey,
    ).then((loadedRecentItems) => {
      if (cancelled) {
        return [];
      }
      if (
        state.recentItems == null ||
        !arraysEqual(loadedRecentItems, state.recentItems)
      ) {
        setTimeout(() => {
          dispatch({
            items: loadedRecentItems,
            type: 'set-recent-items',
          });
        }, 1);
      }
      return loadedRecentItems;
    }).catch((error) => {
      console.error(error);
      Sentry.captureException(error);
    });
    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    entityType,
    state.recentItemsKey,
    state.recentItems,
  ]);

  React.useEffect(() => {
    if (
      !staticItems &&
      nonEmpty(pendingSearch) &&
      !inputTimeout.current &&
      !xhr.current
    ) {
      inputTimeout.current = setTimeout(() => {
        inputTimeout.current = null;

        const pendingSearchTerm = clean(pendingSearch);
        // Check if the input value has changed before proceeding.
        if (
          nonEmpty(pendingSearchTerm) &&
          pendingSearchTerm === clean(inputValue)
        ) {
          doSearch<T>(dispatch, state, xhr);
        }
      }, 300);
    }

    return clearInputTimeout;
  });

  React.useEffect(() => {
    const shouldUpdateScrollPosition = (
      (isOpen && !prevIsOpen.current) ||
      shouldUpdateScrollPositionRef.current
    );
    prevIsOpen.current = isOpen;
    if (shouldUpdateScrollPosition) {
      setScrollPosition(menuId);
      shouldUpdateScrollPositionRef.current = false;
    }
  });

  const menuItemElements = React.useMemo(
    () => items.map((item, index) => (
      <AutocompleteItem
        autocompleteId={inputId}
        dispatch={dispatch}
        formatOptions={
          (
            entityType === 'link_attribute_type' ||
            entityType === 'link_type'
          )
            ? {showDescriptions: state.showDescriptions}
            : undefined
        }
        index={index}
        isHighlighted={highlightedItem != null &&
          item.id === highlightedItem.id}
        isSelected={selectedItem != null &&
          item.type === 'option' &&
          item.entity.id === selectedItem.id}
        // $FlowExpectedError[incompatible-type] until Flow supports https://github.com/facebook/flow/issues/7672
        item={item}
        key={item.id}
        selectItem={selectItem}
      />
    )),
    [
      dispatch,
      entityType,
      highlightedItem,
      inputId,
      items,
      selectItem,
      selectedItem,
      state.showDescriptions,
    ],
  );

  const isLookupPerformed = (
    state.isLookupPerformed == null
      ? (selectedItem != null)
      : state.isLookupPerformed
  );

  const containerElement = (
    <div
      className={
        'autocomplete2' +
        (nonEmpty(containerClass) ? ' ' + containerClass : '')
      }
      onBlur={handleBlur}
      ref={node => {
        containerRef.current = node;
      }}
      style={nonEmpty(state.width) ? {width: state.width} : null}
    >
      <div
        aria-expanded={(isOpen && !disabled) ? 'true' : 'false'}
        aria-haspopup="listbox"
        aria-owns={menuId}
        className={state.required ? 'required' : undefined}
        role="combobox"
      >
        <input
          aria-activedescendant={activeDescendant}
          aria-autocomplete="list"
          aria-controls={menuId}
          aria-labelledby={labelId}
          aria-required={state.required ? 'true' : 'false'}
          autoComplete="off"
          className={
            (
              state.inputClass == null
                ? ''
                : (state.inputClass + ' ')
            ) +
            (
              isLookupPerformed
                ? 'lookup-performed'
                : (state.required ? 'required' : '')
            )
          }
          disabled={disabled}
          id={inputId}
          name={nonEmpty(htmlName) ? (htmlName + '.name') : null}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          placeholder={
            nonEmpty(state.placeholder)
              ? state.placeholder
              : l('Type to search, or paste an MBID')
          }
          ref={inputRefCallback}
          type="text"
          value={inputValue}
        />
        <button
          aria-activedescendant={activeDescendant}
          aria-controls={menuId}
          aria-haspopup="true"
          aria-label={l('Search')}
          className={
            'search' +
            ((
              nonEmpty(pendingSearch) &&
              !disabled &&
              /*
               * Lookups for static item lists complete near-instantly,
               * so flashing a loading spinner is obnoxious.
               */
              !staticItems
            ) ? ' loading' : '')
          }
          data-toggle="true"
          disabled={disabled}
          onClick={handleButtonClick}
          onKeyDown={handleInputKeyDown}
          ref={buttonRef}
          role="button"
          tabIndex={-1}
          title={l('Search')}
          type="button"
        />
        {props.children}
      </div>

      <ul
        aria-controls={statusId}
        aria-labelledby={labelId}
        id={menuId}
        onMouseDown={handleMenuMouseDown}
        role="listbox"
        tabIndex={-1}
      >
        {disabled ? null : menuItemElements}
      </ul>

      <div
        aria-live="assertive"
        aria-relevant="additions text"
        id={statusId}
        role="status"
      >
        {statusMessage}
      </div>

      {nonEmpty(htmlName) ? (
        <>
          <input
            name={htmlName + '.id'}
            type="hidden"
            value={selectedItem ? selectedItem.entity.id : ''}
          />
          <input
            name={htmlName + '.gid'}
            type="hidden"
            value={selectedItem ? selectedItem.entity.gid : ''}
          />
        </>
      ) : null}

      {isAddEntityDialogOpen ? (
        <AddEntityDialog
          callback={addEntityDialogCallback}
          close={closeAddEntityDialog}
          entityType={entityType}
          name={inputValue}
        />
      ) : null}
    </div>
  );

  const labelElement = (
    <label
      className={
        'autocomplete2-label' +
        (state.showLabel === true ? ' visible' : '') +
        (state.required ? ' required' : '')
      }
      htmlFor={inputId}
      id={labelId}
    >
      {addColonText(
        nonEmpty(state.label)
          ? state.label
          : (
            nonEmpty(state.placeholder)
              ? state.placeholder
              : SEARCH_PLACEHOLDERS[entityType]()
          ),
      )}
    </label>
  );

  return (
    <>
      {labelElement}
      {containerElement}
    </>
  );
}

const Autocomplete2: typeof _Autocomplete2 =
  // $FlowExpectedError[incompatible-type]
  React.memo(_Autocomplete2);

export default Autocomplete2;
